import { useCallback, useEffect, useRef, useState } from "react";

const MAX_HISTORY = 50;

export function useUndoHistory<T>(
  state: T,
  restoreState: (snapshot: T) => void
) {
  const pastRef = useRef<T[]>([]);
  const futureRef = useRef<T[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Current state ref for undo/redo (avoids stale closures)
  const stateRef = useRef(state);
  stateRef.current = state;

  // Serialized state from previous render
  const prevSerializedRef = useRef<string>(JSON.stringify(state));

  // Flag: next state change should be captured as an undo entry
  const shouldCaptureRef = useRef(false);

  // After each render, check if we should capture
  useEffect(() => {
    const currentSerialized = JSON.stringify(state);

    if (
      shouldCaptureRef.current &&
      prevSerializedRef.current !== currentSerialized
    ) {
      // State changed since saveSnapshot was called — capture the previous state
      const snapshot: T = JSON.parse(prevSerializedRef.current);
      pastRef.current = [
        ...pastRef.current.slice(-(MAX_HISTORY - 1)),
        snapshot,
      ];
      futureRef.current = [];
      setCanUndo(true);
      setCanRedo(false);
      shouldCaptureRef.current = false;
    }

    prevSerializedRef.current = currentSerialized;
  });

  const saveSnapshot = useCallback(() => {
    shouldCaptureRef.current = true;
  }, []);

  const undo = useCallback(() => {
    if (pastRef.current.length === 0) return;

    const current: T = JSON.parse(JSON.stringify(stateRef.current));
    const previous = pastRef.current[pastRef.current.length - 1];

    pastRef.current = pastRef.current.slice(0, -1);
    futureRef.current = [...futureRef.current, current];

    // Prevent the useEffect from capturing this restore as a new undo entry
    shouldCaptureRef.current = false;

    setCanUndo(pastRef.current.length > 0);
    setCanRedo(true);

    restoreState(previous);
  }, [restoreState]);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return;

    const current: T = JSON.parse(JSON.stringify(stateRef.current));
    const next = futureRef.current[futureRef.current.length - 1];

    futureRef.current = futureRef.current.slice(0, -1);
    pastRef.current = [...pastRef.current, current];

    shouldCaptureRef.current = false;

    setCanUndo(true);
    setCanRedo(futureRef.current.length > 0);

    restoreState(next);
  }, [restoreState]);

  return { saveSnapshot, undo, redo, canUndo, canRedo };
}
